/**
 * @fileoverview Change Order Modal Component
 *
 * Modal for creating and editing project change orders.
 * Supports both add and edit modes with form fields for all
 * change order properties and file upload for signed documentation.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../../services/cloudinaryService
 * @version 1.0.0
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaSpinner,
  FaCheckCircle,
  FaPlusCircle,
  FaExclamationCircle,
  FaTrash,
  FaExchangeAlt,
  FaDollarSign,
  FaClock,
  FaAlignLeft,
  FaQuestionCircle,
  FaClipboard,
  FaFileContract,
  FaCalendarCheck,
  FaTimesCircle,
} from "react-icons/fa";
import * as Sentry from "@sentry/react";
import {
  uploadFile,
  buildUploadContext,
} from "../../services/cloudinaryService";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import "./ChangeOrderModal.css";

/**
 * Fallback status options (used while field options are loading)
 * @constant {Array<string>}
 */
const FALLBACK_STATUS_OPTIONS = [
  "Submitted",
  "Approved",
  "Denied",
  "Completed",
];

/**
 * Fallback reason options (used while field options are loading)
 * @constant {Array<string>}
 */
const FALLBACK_REASON_OPTIONS = [];

/**
 * Accepted file types for documentation uploads
 * @constant {string}
 */
const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png";

/**
 * Maximum file size in bytes (10MB)
 * @constant {number}
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Get appropriate icon for file type
 * @param {string} filename - Name of the file
 * @returns {JSX.Element} Icon component
 */
const getFileIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf") return <FaFilePdf className="co-modal-file-icon pdf" />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return <FaFileImage className="co-modal-file-icon image" />;
  }
  return <FaFileAlt className="co-modal-file-icon doc" />;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Change Order Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.projectId - Airtable project record ID
 * @param {string} [props.projectName] - Project name for display
 * @param {Object} [props.changeOrder] - Existing change order for edit mode (null for create)
 * @param {Function} props.onSave - Save handler (receives form data)
 * @param {boolean} [props.isSaving] - Whether save is in progress
 * @param {Object} [props.fieldOptions] - Airtable field options { status: string[], reason: string[] }
 */
const ChangeOrderModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  changeOrder = null,
  onSave,
  isSaving = false,
  fieldOptions = {},
}) => {
  const statusOptions = fieldOptions.status || FALLBACK_STATUS_OPTIONS;
  const reasonOptions = fieldOptions.reason || FALLBACK_REASON_OPTIONS;
  const isEditMode = Boolean(changeOrder);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    reason: "",
    status: "Submitted",
    costImpact: "",
    timeImpact: "",
    dateSubmitted: new Date().toISOString().split("T")[0],
    internalNotes: "",
    denialReason: "",
  });

  // File upload state - Signed Documentation
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // File upload state - Other Files
  const [selectedOtherFiles, setSelectedOtherFiles] = useState([]);
  const [isDraggingOther, setIsDraggingOther] = useState(false);
  const [otherUploadProgress, setOtherUploadProgress] = useState({});

  // Track existing docs marked for deletion
  const [deletedDocIds, setDeletedDocIds] = useState([]);
  const [deletedOtherFileIds, setDeletedOtherFileIds] = useState([]);
  const [deletedFilesPhotosIds, setDeletedFilesPhotosIds] = useState([]);

  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, success, error
  const [errorMessage, setErrorMessage] = useState("");

  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  /**
   * Initialize form data when modal opens or changeOrder changes
   */
  useEffect(() => {
    if (isOpen && changeOrder) {
      setFormData({
        title: changeOrder.title || "",
        description: changeOrder.description || "",
        reason: changeOrder.reason || "",
        status: changeOrder.status || "Submitted",
        costImpact: changeOrder.costImpact ?? "",
        timeImpact: changeOrder.timeImpact ?? "",
        dateSubmitted: changeOrder.dateSubmitted
          ? changeOrder.dateSubmitted.split("T")[0]
          : new Date().toISOString().split("T")[0],
        internalNotes: changeOrder.internalNotes || "",
        denialReason: changeOrder.denialReason || "",
      });
    } else if (isOpen && !changeOrder) {
      setFormData({
        title: "",
        description: "",
        reason: "",
        status: "Submitted",
        costImpact: "",
        timeImpact: "",
        dateSubmitted: new Date().toISOString().split("T")[0],
        internalNotes: "",
        denialReason: "",
      });
    }

    // Reset file/upload state on open
    if (isOpen) {
      setSelectedFiles([]);
      setUploadProgress({});
      setSelectedOtherFiles([]);
      setOtherUploadProgress({});
      setDeletedDocIds([]);
      setDeletedOtherFileIds([]);
      setDeletedFilesPhotosIds([]);
      setSaveStatus("idle");
      setErrorMessage("");
    }
  }, [isOpen, changeOrder]);

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Validate file before adding
   */
  const validateFile = (file) => {
    const errors = [];
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File exceeds 10MB limit (${formatFileSize(file.size)})`);
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      errors.push(`File type .${ext} is not supported`);
    }
    return { isValid: errors.length === 0, errors };
  };

  /**
   * Add files to a file list with validation
   */
  const createAddFiles = useCallback(
    (currentFiles, setFiles) => (files) => {
      const validFiles = [];
      const errors = [];

      files.forEach((file) => {
        const validation = validateFile(file);
        if (validation.isValid) {
          const isDuplicate = currentFiles.some(
            (f) => f.name === file.name && f.size === file.size,
          );
          if (!isDuplicate) {
            validFiles.push(file);
          }
        } else {
          errors.push(`${file.name}: ${validation.errors.join(", ")}`);
        }
      });

      if (errors.length > 0) {
        setErrorMessage(errors.join("\n"));
        setTimeout(() => setErrorMessage(""), 5000);
      }

      setFiles((prev) => [...prev, ...validFiles]);
    },
    [],
  );

  const addFiles = useCallback(
    (files) => createAddFiles(selectedFiles, setSelectedFiles)(files),
    [createAddFiles, selectedFiles],
  );

  const addOtherFiles = useCallback(
    (files) => createAddFiles(selectedOtherFiles, setSelectedOtherFiles)(files),
    [createAddFiles, selectedOtherFiles],
  );

  // Signed Documentation handlers
  const handleFileSelect = (e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      addFiles(Array.from(e.dataTransfer.files));
    },
    [addFiles],
  );

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  // Other Files handlers
  const handleOtherFileSelect = (e) => {
    addOtherFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleOtherDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOther(true);
  }, []);

  const handleOtherDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOther(false);
  }, []);

  const handleOtherDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOther(false);
      addOtherFiles(Array.from(e.dataTransfer.files));
    },
    [addOtherFiles],
  );

  const handleRemoveOtherFile = (index) => {
    setSelectedOtherFiles((prev) => prev.filter((_, i) => i !== index));
    setOtherUploadProgress((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  /**
   * Handle form submission
   * Uploads files to Cloudinary first, then calls onSave
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setErrorMessage("Title is required");
      return;
    }

    setSaveStatus("saving");
    setErrorMessage("");

    return Sentry.startSpan(
      {
        op: "changeOrder.save",
        name: isEditMode ? "Update Change Order" : "Create Change Order",
      },
      async (span) => {
        span.setAttribute("projectId", projectId);
        span.setAttribute("isEdit", isEditMode);

        try {
          let signedDocumentation = [];
          let otherFilesUploaded = [];

          // Upload signed documentation files to Cloudinary
          if (selectedFiles.length > 0) {
            for (let i = 0; i < selectedFiles.length; i++) {
              const file = selectedFiles[i];
              try {
                setUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "uploading", progress: 0 },
                }));

                const uploadContext = buildUploadContext({
                  projectId,
                  projectName: projectName || "",
                  category: "signed_documentation",
                  categoryLabel: "Signed Documentation",
                  sourceTable: "change_orders",
                  uploadedByName: teamMemberName,
                  file,
                });

                const cloudinaryResult = await uploadFile(file, {
                  projectId,
                  sourceTable: "change_orders",
                  category: "signed_documentation",
                  uploadContext,
                });

                signedDocumentation.push({
                  url: cloudinaryResult.url,
                  filename: file.name,
                });

                setUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "complete", progress: 100 },
                }));
              } catch (fileError) {
                console.error(`Error uploading ${file.name}:`, fileError);
                Sentry.captureException(fileError);
                setUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "error", progress: 0 },
                }));
              }
            }
          }

          // Upload other files to Cloudinary
          if (selectedOtherFiles.length > 0) {
            for (let i = 0; i < selectedOtherFiles.length; i++) {
              const file = selectedOtherFiles[i];
              try {
                setOtherUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "uploading", progress: 0 },
                }));

                const uploadContext = buildUploadContext({
                  projectId,
                  projectName: projectName || "",
                  category: "other",
                  categoryLabel: "Other Change Order Files",
                  sourceTable: "change_orders",
                  uploadedByName: teamMemberName,
                  file,
                });

                const cloudinaryResult = await uploadFile(file, {
                  projectId,
                  sourceTable: "change_orders",
                  category: "other",
                  uploadContext,
                });

                otherFilesUploaded.push({
                  url: cloudinaryResult.url,
                  filename: file.name,
                });

                setOtherUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "complete", progress: 100 },
                }));
              } catch (fileError) {
                console.error(`Error uploading ${file.name}:`, fileError);
                Sentry.captureException(fileError);
                setOtherUploadProgress((prev) => ({
                  ...prev,
                  [i]: { status: "error", progress: 0 },
                }));
              }
            }
          }

          // Build save data
          const saveData = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            reason: formData.reason,
            status: formData.status,
            costImpact: formData.costImpact
              ? parseFloat(formData.costImpact)
              : 0,
            timeImpact: formData.timeImpact
              ? parseInt(formData.timeImpact, 10)
              : 0,
            dateSubmitted: formData.dateSubmitted,
            internalNotes: formData.internalNotes.trim(),
          };

          // Only include denialReason if status is Denied
          if (formData.status === "Denied") {
            saveData.denialReason = formData.denialReason.trim();
          }

          // Include uploaded files if any
          if (signedDocumentation.length > 0) {
            saveData.signedDocumentation = signedDocumentation;
          }
          if (otherFilesUploaded.length > 0) {
            saveData.otherFiles = otherFilesUploaded;
          }

          // Include deleted attachment IDs for edit mode
          if (deletedDocIds.length > 0) {
            saveData.deletedDocIds = deletedDocIds;
          }
          if (deletedOtherFileIds.length > 0) {
            saveData.deletedOtherFileIds = deletedOtherFileIds;
          }
          if (deletedFilesPhotosIds.length > 0) {
            saveData.deletedFilesPhotos = deletedFilesPhotosIds;
          }

          await onSave(saveData, isEditMode ? changeOrder.id : null);

          setSaveStatus("success");
          span.setAttribute("success", true);

          setTimeout(() => {
            handleClose();
          }, 1200);
        } catch (error) {
          console.error("[ChangeOrderModal] Save error:", error);
          Sentry.captureException(error);
          span.setAttribute("success", false);
          span.setAttribute("error", error.message);
          setSaveStatus("error");
          setErrorMessage(error.message || "Failed to save change order");
        }
      },
    );
  };

  /**
   * Handle modal close - reset state
   */
  const handleClose = () => {
    if (saveStatus === "saving") return;
    setFormData({
      title: "",
      description: "",
      reason: "",
      status: "Submitted",
      costImpact: "",
      timeImpact: "",
      dateSubmitted: new Date().toISOString().split("T")[0],
      internalNotes: "",
      denialReason: "",
    });
    setSelectedFiles([]);
    setUploadProgress({});
    setSelectedOtherFiles([]);
    setOtherUploadProgress({});
    setDeletedDocIds([]);
    setDeletedOtherFileIds([]);
    setDeletedFilesPhotosIds([]);
    setSaveStatus("idle");
    setErrorMessage("");
    onClose();
  };

  if (!isOpen) return null;

  const isBusy = saveStatus === "saving" || isSaving;

  return (
    <div className="co-modal-overlay" onClick={handleClose}>
      <div className="co-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="co-modal-header">
          <div className="co-modal-header-left">
            <div className="co-modal-header-icon">
              <FaExchangeAlt />
            </div>
            <h3>{isEditMode ? "Edit Change Order" : "New Change Order"}</h3>
          </div>
          <button
            className="co-modal-close-btn"
            onClick={handleClose}
            disabled={isBusy}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form className="co-modal-body" onSubmit={handleSubmit}>
          {/* Project Display */}
          {projectName && (
            <div className="co-modal-project-banner">
              {projectName}
              {isEditMode && changeOrder.coNumber && (
                <span className="co-modal-co-number">
                  {changeOrder.coNumber}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          <div className="co-modal-form-group">
            <label className="co-modal-label">
              <FaFileAlt /> Title <span className="required">*</span>
            </label>
            <input
              type="text"
              name="title"
              className="co-modal-input"
              value={formData.title}
              onChange={handleChange}
              placeholder="Change order title..."
              disabled={isBusy}
              required
            />
          </div>

          {/* Description */}
          <div className="co-modal-form-group">
            <label className="co-modal-label">
              <FaAlignLeft /> Description
            </label>
            <textarea
              name="description"
              className="co-modal-textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the changes..."
              rows={3}
              disabled={isBusy}
            />
          </div>

          {/* Two-column: Reason + Status */}
          <div className="co-modal-row">
            <div className="co-modal-form-group">
              <label className="co-modal-label">
                <FaQuestionCircle /> Reason
              </label>
              <select
                name="reason"
                className="co-modal-select"
                value={formData.reason}
                onChange={handleChange}
                disabled={isBusy}
              >
                <option value="">Select reason...</option>
                {reasonOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="co-modal-form-group">
              <label className="co-modal-label">
                <FaCheckCircle /> Status
              </label>
              <select
                name="status"
                className="co-modal-select"
                value={formData.status}
                onChange={handleChange}
                disabled={isBusy}
              >
                {statusOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Two-column: Cost + Time Impact */}
          <div className="co-modal-row">
            <div className="co-modal-form-group">
              <label className="co-modal-label">
                <FaDollarSign /> Cost Impact
              </label>
              <input
                type="number"
                name="costImpact"
                className="co-modal-input"
                value={formData.costImpact}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                disabled={isBusy}
              />
            </div>

            <div className="co-modal-form-group">
              <label className="co-modal-label">
                <FaClock /> Time Impact (Days)
              </label>
              <input
                type="number"
                name="timeImpact"
                className="co-modal-input"
                value={formData.timeImpact}
                onChange={handleChange}
                placeholder="0"
                min="0"
                disabled={isBusy}
              />
            </div>
          </div>

          {/* Date Submitted */}
          <div className="co-modal-form-group">
            <label className="co-modal-label">
              <FaCalendarCheck /> Date Submitted
            </label>
            <input
              type="date"
              name="dateSubmitted"
              className="co-modal-input"
              value={formData.dateSubmitted}
              onChange={handleChange}
              disabled={isBusy}
            />
          </div>

          {/* Denial Reason (shown only when status is Denied) */}
          {formData.status === "Denied" && (
            <div className="co-modal-form-group">
              <label className="co-modal-label">
                <FaTimesCircle /> Denial Reason
              </label>
              <textarea
                name="denialReason"
                className="co-modal-textarea"
                value={formData.denialReason}
                onChange={handleChange}
                placeholder="Reason for denial..."
                rows={2}
                disabled={isBusy}
              />
            </div>
          )}

          {/* Internal Notes */}
          <div className="co-modal-form-group">
            <label className="co-modal-label">
              <FaClipboard /> Internal Notes
            </label>
            <textarea
              name="internalNotes"
              className="co-modal-textarea"
              value={formData.internalNotes}
              onChange={handleChange}
              placeholder="Internal notes (not visible to client)..."
              rows={2}
              disabled={isBusy}
            />
          </div>

          {/* Documentation Files */}
          {isEditMode ? (
            <div className="co-modal-row co-modal-files-row">
              {/* Existing Documentation */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileContract /> Existing Documentation
                </label>
                <div className="co-modal-existing-docs">
                  {changeOrder.signedDocumentation &&
                  changeOrder.signedDocumentation.filter(
                    (doc) => !deletedDocIds.includes(doc.id),
                  ).length > 0 ? (
                    changeOrder.signedDocumentation
                      .filter((doc) => !deletedDocIds.includes(doc.id))
                      .map((doc) => (
                        <div
                          key={doc.id}
                          className="co-modal-existing-doc-row"
                        >
                          <a
                            href={doc.url}
                            className="co-modal-existing-doc"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span>{doc.name || doc.filename}</span>
                          </a>
                          <button
                            type="button"
                            className="co-modal-existing-doc-delete"
                            onClick={() =>
                              setDeletedDocIds((prev) => [...prev, doc.id])
                            }
                            disabled={isBusy}
                            aria-label={`Remove ${doc.name || doc.filename}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))
                  ) : (
                    <span className="co-modal-no-files">No files</span>
                  )}
                </div>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="co-modal-new-files">
                    <div className="co-modal-files-header">
                      <span className="co-modal-label">
                        New Files ({selectedFiles.length})
                      </span>
                      {saveStatus === "idle" && selectedFiles.length > 1 && (
                        <button
                          type="button"
                          className="co-modal-clear-all-btn"
                          onClick={() => setSelectedFiles([])}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="co-modal-files-list">
                      {selectedFiles.map((file, index) => {
                        const progress = uploadProgress[index];
                        return (
                          <div
                            key={`${file.name}-${index}`}
                            className={`co-modal-file-item ${
                              progress?.status || "pending"
                            }`}
                          >
                            {getFileIcon(file.name)}
                            <div className="co-modal-file-info">
                              <span className="co-modal-file-name">
                                {file.name}
                              </span>
                              <span className="co-modal-file-size">
                                {formatFileSize(file.size)}
                              </span>
                            </div>
                            <div className="co-modal-file-status">
                              {progress?.status === "uploading" && (
                                <FaSpinner className="co-modal-spinner" />
                              )}
                              {progress?.status === "complete" && (
                                <FaCheckCircle className="co-modal-success-icon" />
                              )}
                              {progress?.status === "error" && (
                                <FaExclamationCircle className="co-modal-error-icon" />
                              )}
                              {!progress && saveStatus === "idle" && (
                                <button
                                  type="button"
                                  className="co-modal-remove-btn"
                                  onClick={() => handleRemoveFile(index)}
                                  aria-label={`Remove ${file.name}`}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Additional Documentation */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileContract /> Upload Additional Documentation
                </label>
                <div
                  className={`co-modal-dropzone ${isDragging ? "dragging" : ""} ${
                    isBusy ? "disabled" : ""
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="co-doc-upload"
                    className="co-modal-file-input"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    onChange={handleFileSelect}
                    disabled={isBusy}
                  />
                  <label
                    htmlFor="co-doc-upload"
                    className="co-modal-dropzone-content"
                  >
                    <FaCloudUploadAlt className="co-modal-dropzone-icon" />
                    <p className="co-modal-dropzone-text">
                      Drag & drop files here or click to browse
                    </p>
                    <p className="co-modal-dropzone-hint">
                      PDF, JPG, PNG - Max 10MB per file
                    </p>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Create mode: full-width upload */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileContract /> Signed Documentation
                </label>
                <div
                  className={`co-modal-dropzone ${isDragging ? "dragging" : ""} ${
                    isBusy ? "disabled" : ""
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="co-doc-upload"
                    className="co-modal-file-input"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    onChange={handleFileSelect}
                    disabled={isBusy}
                  />
                  <label
                    htmlFor="co-doc-upload"
                    className="co-modal-dropzone-content"
                  >
                    <FaCloudUploadAlt className="co-modal-dropzone-icon" />
                    <p className="co-modal-dropzone-text">
                      Drag & drop files here or click to browse
                    </p>
                    <p className="co-modal-dropzone-hint">
                      PDF, JPG, PNG - Max 10MB per file
                    </p>
                  </label>
                </div>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="co-modal-form-group">
                  <div className="co-modal-files-header">
                    <span className="co-modal-label">
                      New Files ({selectedFiles.length})
                    </span>
                    {saveStatus === "idle" && selectedFiles.length > 1 && (
                      <button
                        type="button"
                        className="co-modal-clear-all-btn"
                        onClick={() => setSelectedFiles([])}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="co-modal-files-list">
                    {selectedFiles.map((file, index) => {
                      const progress = uploadProgress[index];
                      return (
                        <div
                          key={`${file.name}-${index}`}
                          className={`co-modal-file-item ${
                            progress?.status || "pending"
                          }`}
                        >
                          {getFileIcon(file.name)}
                          <div className="co-modal-file-info">
                            <span className="co-modal-file-name">
                              {file.name}
                            </span>
                            <span className="co-modal-file-size">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                          <div className="co-modal-file-status">
                            {progress?.status === "uploading" && (
                              <FaSpinner className="co-modal-spinner" />
                            )}
                            {progress?.status === "complete" && (
                              <FaCheckCircle className="co-modal-success-icon" />
                            )}
                            {progress?.status === "error" && (
                              <FaExclamationCircle className="co-modal-error-icon" />
                            )}
                            {!progress && saveStatus === "idle" && (
                              <button
                                type="button"
                                className="co-modal-remove-btn"
                                onClick={() => handleRemoveFile(index)}
                                aria-label={`Remove ${file.name}`}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Other Files */}
          {isEditMode ? (
            <div className="co-modal-row co-modal-files-row">
              {/* Existing Other Files */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileAlt /> Existing Other Files
                </label>
                <div className="co-modal-existing-docs">
                  {(() => {
                    const filteredOther = (changeOrder.otherFiles || []).filter(
                      (doc) => !deletedOtherFileIds.includes(doc.id),
                    );
                    const filesPhotos = (changeOrder.filesPhotos || []).filter(
                      (doc) => !deletedFilesPhotosIds.some((d) => d.attachmentId === doc.id),
                    );
                    return filteredOther.length > 0 ||
                      filesPhotos.length > 0 ? (
                      <>
                        {filteredOther.map((doc) => (
                          <div
                            key={doc.id}
                            className="co-modal-existing-doc-row"
                          >
                            <a
                              href={doc.url}
                              className="co-modal-existing-doc"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span>{doc.name || doc.filename}</span>
                            </a>
                            <button
                              type="button"
                              className="co-modal-existing-doc-delete"
                              onClick={() =>
                                setDeletedOtherFileIds((prev) => [
                                  ...prev,
                                  doc.id,
                                ])
                              }
                              disabled={isBusy}
                              aria-label={`Remove ${doc.name || doc.filename}`}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                        {filesPhotos
                          .filter(
                            (doc) =>
                              !deletedFilesPhotosIds.some((d) => d.attachmentId === doc.id),
                          )
                          .map((doc) => (
                            <div
                              key={doc.id}
                              className="co-modal-existing-doc-row"
                            >
                              <a
                                href={doc.url}
                                className="co-modal-existing-doc"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <span>
                                  {doc.name || doc.filename}
                                </span>
                              </a>
                              <button
                                type="button"
                                className="co-modal-existing-doc-delete"
                                onClick={() =>
                                  setDeletedFilesPhotosIds((prev) => [
                                    ...prev,
                                    { recordId: doc.recordId, attachmentId: doc.id },
                                  ])
                                }
                                disabled={isBusy}
                                aria-label={`Remove ${doc.name || doc.filename}`}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ))}
                      </>
                    ) : (
                      <span className="co-modal-no-files">No files</span>
                    );
                  })()}
                </div>

                {/* Selected Other Files List */}
                {selectedOtherFiles.length > 0 && (
                  <div className="co-modal-new-files">
                    <div className="co-modal-files-header">
                      <span className="co-modal-label">
                        New Other Files ({selectedOtherFiles.length})
                      </span>
                      {saveStatus === "idle" &&
                        selectedOtherFiles.length > 1 && (
                          <button
                            type="button"
                            className="co-modal-clear-all-btn"
                            onClick={() => setSelectedOtherFiles([])}
                          >
                            Clear All
                          </button>
                        )}
                    </div>
                    <div className="co-modal-files-list">
                      {selectedOtherFiles.map((file, index) => {
                        const progress = otherUploadProgress[index];
                        return (
                          <div
                            key={`other-${file.name}-${index}`}
                            className={`co-modal-file-item ${
                              progress?.status || "pending"
                            }`}
                          >
                            {getFileIcon(file.name)}
                            <div className="co-modal-file-info">
                              <span className="co-modal-file-name">
                                {file.name}
                              </span>
                              <span className="co-modal-file-size">
                                {formatFileSize(file.size)}
                              </span>
                            </div>
                            <div className="co-modal-file-status">
                              {progress?.status === "uploading" && (
                                <FaSpinner className="co-modal-spinner" />
                              )}
                              {progress?.status === "complete" && (
                                <FaCheckCircle className="co-modal-success-icon" />
                              )}
                              {progress?.status === "error" && (
                                <FaExclamationCircle className="co-modal-error-icon" />
                              )}
                              {!progress && saveStatus === "idle" && (
                                <button
                                  type="button"
                                  className="co-modal-remove-btn"
                                  onClick={() => handleRemoveOtherFile(index)}
                                  aria-label={`Remove ${file.name}`}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Additional Other Files */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileAlt /> Upload Additional Other Files
                </label>
                <div
                  className={`co-modal-dropzone ${isDraggingOther ? "dragging" : ""} ${
                    isBusy ? "disabled" : ""
                  }`}
                  onDragOver={handleOtherDragOver}
                  onDragLeave={handleOtherDragLeave}
                  onDrop={handleOtherDrop}
                >
                  <input
                    type="file"
                    id="co-other-upload"
                    className="co-modal-file-input"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    onChange={handleOtherFileSelect}
                    disabled={isBusy}
                  />
                  <label
                    htmlFor="co-other-upload"
                    className="co-modal-dropzone-content"
                  >
                    <FaCloudUploadAlt className="co-modal-dropzone-icon" />
                    <p className="co-modal-dropzone-text">
                      Drag & drop files here or click to browse
                    </p>
                    <p className="co-modal-dropzone-hint">
                      PDF, JPG, PNG - Max 10MB per file
                    </p>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Create mode: full-width upload */}
              <div className="co-modal-form-group">
                <label className="co-modal-label">
                  <FaFileAlt /> Other Files
                </label>
                <div
                  className={`co-modal-dropzone ${isDraggingOther ? "dragging" : ""} ${
                    isBusy ? "disabled" : ""
                  }`}
                  onDragOver={handleOtherDragOver}
                  onDragLeave={handleOtherDragLeave}
                  onDrop={handleOtherDrop}
                >
                  <input
                    type="file"
                    id="co-other-upload"
                    className="co-modal-file-input"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    onChange={handleOtherFileSelect}
                    disabled={isBusy}
                  />
                  <label
                    htmlFor="co-other-upload"
                    className="co-modal-dropzone-content"
                  >
                    <FaCloudUploadAlt className="co-modal-dropzone-icon" />
                    <p className="co-modal-dropzone-text">
                      Drag & drop files here or click to browse
                    </p>
                    <p className="co-modal-dropzone-hint">
                      PDF, JPG, PNG - Max 10MB per file
                    </p>
                  </label>
                </div>
              </div>

              {/* Selected Other Files List */}
              {selectedOtherFiles.length > 0 && (
                <div className="co-modal-form-group">
                  <div className="co-modal-files-header">
                    <span className="co-modal-label">
                      New Other Files ({selectedOtherFiles.length})
                    </span>
                    {saveStatus === "idle" && selectedOtherFiles.length > 1 && (
                      <button
                        type="button"
                        className="co-modal-clear-all-btn"
                        onClick={() => setSelectedOtherFiles([])}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="co-modal-files-list">
                    {selectedOtherFiles.map((file, index) => {
                      const progress = otherUploadProgress[index];
                      return (
                        <div
                          key={`other-${file.name}-${index}`}
                          className={`co-modal-file-item ${
                            progress?.status || "pending"
                          }`}
                        >
                          {getFileIcon(file.name)}
                          <div className="co-modal-file-info">
                            <span className="co-modal-file-name">
                              {file.name}
                            </span>
                            <span className="co-modal-file-size">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                          <div className="co-modal-file-status">
                            {progress?.status === "uploading" && (
                              <FaSpinner className="co-modal-spinner" />
                            )}
                            {progress?.status === "complete" && (
                              <FaCheckCircle className="co-modal-success-icon" />
                            )}
                            {progress?.status === "error" && (
                              <FaExclamationCircle className="co-modal-error-icon" />
                            )}
                            {!progress && saveStatus === "idle" && (
                              <button
                                type="button"
                                className="co-modal-remove-btn"
                                onClick={() => handleRemoveOtherFile(index)}
                                aria-label={`Remove ${file.name}`}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="co-modal-error-message">
              <FaExclamationCircle />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Success Message */}
          {saveStatus === "success" && (
            <div className="co-modal-success-message">
              <FaCheckCircle />
              <span>
                Change order {isEditMode ? "updated" : "created"} successfully!
              </span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="co-modal-footer">
          <button
            type="button"
            className="co-modal-cancel-btn"
            onClick={handleClose}
            disabled={isBusy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="co-modal-save-btn"
            onClick={handleSubmit}
            disabled={
              isBusy || !formData.title.trim() || saveStatus === "success"
            }
          >
            {isBusy ? (
              <>
                <FaSpinner className="co-modal-btn-spinner" />
                {saveStatus === "saving" ? "Saving..." : "Processing..."}
              </>
            ) : (
              <>
                {isEditMode ? <FaCheckCircle /> : <FaPlusCircle />}
                {isEditMode ? "Update Change Order" : "Create Change Order"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeOrderModal;
